// Anonymous user id — placeholder for the future account system.
//
// Decision (Session 19E): per MANIFESTO §1.3, we do NOT add long-term accounts.
// But every CourseDraft carries `anonId` so future migration to email-bound
// accounts is one-shot (anon → email). Without it, drafts created today can't
// be associated with the user when accounts arrive.
//
// Implementation: lazy-generate via crypto.randomUUID() on first read; persist
// to localStorage. If localStorage is locked (private mode / sandbox), fall
// back to an in-memory id stable for the session.

const ANON_ID_KEY = 'examforge.anonId';

let memoryFallback: string | null = null;

export function getOrCreateAnonId(): string {
  if (typeof window === 'undefined') {
    // SSR / test environment — return a stable session-scoped id.
    if (!memoryFallback) memoryFallback = `anon-${Date.now().toString(36)}`;
    return memoryFallback;
  }
  try {
    const existing = window.localStorage.getItem(ANON_ID_KEY);
    if (existing) return existing;
    const fresh = generateId();
    window.localStorage.setItem(ANON_ID_KEY, fresh);
    return fresh;
  } catch {
    // localStorage blocked — degrade to in-memory.
    if (!memoryFallback) memoryFallback = generateId();
    return memoryFallback;
  }
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers without crypto.randomUUID — Math.random + time mix.
  // Not cryptographically strong but anon ids don't need to be.
  return `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export { ANON_ID_KEY };
